import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useTriggerIssueVouchers } from '@/lib/disruptions/use-disruptions';
import type { BenefitOverrideApplicationRow, CareBoard } from '@/lib/disruptions/types';
import { Badge } from '@/components/ui/badge';
import { OUTCOME_VARIANT, outcomeLabelKey } from './care-benefit-override-panel';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { priorityShortLabel } from '../labels';
import { careLaneReason, type CareDeskRow, type CareLane } from './care-desk-model';
import { CareResolvePanel } from './care-resolve-panel';

// The care desk's center, shaped like an INBOX (feat/care-desk K5; the hotel
// work-queue grammar): the queue on the left is a compact, buttonless list —
// the whole row selects; solving happens ONCE, with context, in the resolve
// panel on the right. The first open PNR is auto-selected and resolving
// auto-advances to the next. The KYC-blocked tail is a collapsed strip —
// visible (it still blocks the goal), never in the way.

const LANE_DOT: Partial<Record<CareLane, string>> = {
  FAILED: 'bg-rose-500',
  NEEDS_ISSUE: 'bg-amber-500',
  ATTENTION: 'bg-violet-500',
  BLOCKED_KYC: 'bg-sky-500',
};

/** Display-only assistance chips for one group: priority tags first, then the
 *  SSR / dietary codes (BBML/VGML/KSML/AVML, WCHR, …) from the roster row. */
function ssrChips(d: CareDeskRow): string[] {
  const fromNeeds = d.row.needs ?? [];
  const fromPax = (d.row.passengers ?? []).flatMap((p) => p.specialNeeds ?? []);
  return [...new Set([...fromNeeds, ...fromPax])];
}

export function CareWorkQueue({
  queue,
  blocked,
  disruptionUrn,
  board,
  pinned = null,
  onClearPinned,
  overrideByGroup,
  onOpenOverride,
}: {
  queue: CareDeskRow[];
  blocked: CareDeskRow[];
  disruptionUrn: string;
  board: CareBoard | null;
  /** K6: a selection PINNED from outside the lanes (counter bar / roster deep
   *  link) — may be a row in NO open lane; it wins over the internal selection
   *  and renders the resolve panel in read-only facts mode. */
  pinned?: CareDeskRow | null;
  /** Called when the operator clicks a queue row — hands selection back here. */
  onClearPinned?: () => void;
  /** #819: the latest applied benefit-override result per group (chip on the row). */
  overrideByGroup?: Map<string, { overrideUrn: string; row: BenefitOverrideApplicationRow }>;
  /** #819: the chip asks the Autorizaciones panel to expand that override. */
  onOpenOverride?: (overrideUrn: string) => void;
}) {
  const { t } = useTranslation('care');
  // Actionable = the operator can move money NOW (FAILED retries + fresh issues).
  // ATTENTION rides the queue tail — discretionary, never in the mass count.
  const actionable = useMemo(
    () => queue.filter((d) => d.lane === 'FAILED' || d.lane === 'NEEDS_ISSUE'),
    [queue],
  );

  const [selectedUrn, setSelectedUrn] = useState<string | null>(null);
  const selectable = useMemo(() => [...queue, ...blocked], [queue, blocked]);
  const selected =
    pinned ?? selectable.find((d) => d.row.subCaseUrn === selectedUrn) ?? queue[0] ?? null;

  // Auto-advance: when the selected PNR leaves its worked lane (card issued /
  // outcome recorded), selection falls through to the next open one via the ??
  // above; clearing the stored urn keeps the fallback in charge.
  useEffect(() => {
    if (selectedUrn && !selectable.some((d) => d.row.subCaseUrn === selectedUrn)) {
      setSelectedUrn(null);
    }
  }, [selectedUrn, selectable]);

  const issueAll = useTriggerIssueVouchers(disruptionUrn);
  const [blockedOpen, setBlockedOpen] = useState(false);

  const select = (urn: string): void => {
    onClearPinned?.();
    setSelectedUrn(urn);
  };

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[minmax(15rem,2fr)_minmax(0,3fr)]">
      <div className="flex min-h-0 flex-col gap-2" data-testid="care-work-queue">
        <div className="flex items-center justify-between gap-2 px-1">
          <p className="text-sm font-semibold">
            {t('queue.toIssue')}
            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[11px] font-bold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {actionable.length}
            </span>
          </p>
          {actionable.length > 1 ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => issueAll.mutate()}
              disabled={issueAll.isPending}
              data-testid="care-issue-all"
              title={t('queue.issueAllTitle')}
              className="text-xs text-muted-foreground"
            >
              {issueAll.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : null}
              {t('queue.issueAll', { n: actionable.length })}
            </Button>
          ) : null}
        </div>

        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
          {queue.map((d) => (
            <QueueRow
              key={d.row.subCaseUrn}
              desk={d}
              selected={selected?.row.subCaseUrn === d.row.subCaseUrn}
              onSelect={() => select(d.row.subCaseUrn)}
              override={overrideByGroup?.get(d.row.subCaseUrn)}
              onOpenOverride={onOpenOverride}
            />
          ))}
          {queue.length === 0 ? (
            <li className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              {t('queue.clear')}
            </li>
          ) : null}
        </ul>

        {blocked.length > 0 ? (
          <section className="rounded-lg border border-border bg-card" data-testid="care-blocked-strip">
            <button
              type="button"
              onClick={() => setBlockedOpen((v) => !v)}
              aria-expanded={blockedOpen}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
              data-testid="care-blocked-toggle"
            >
              <p className="text-sm">
                <span aria-hidden className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sky-500" />
                <span className="font-semibold tabular-nums">{blocked.length}</span>{' '}
                <span className="text-muted-foreground">{t('queue.blocked')}</span>
              </p>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform',
                  blockedOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </button>
            {blockedOpen ? (
              <ul className="space-y-1 border-t border-border px-2 py-2">
                {blocked.map((d) => (
                  <QueueRow
                    key={d.row.subCaseUrn}
                    desk={d}
                    selected={selected?.row.subCaseUrn === d.row.subCaseUrn}
                    onSelect={() => select(d.row.subCaseUrn)}
                    override={overrideByGroup?.get(d.row.subCaseUrn)}
                    onOpenOverride={onOpenOverride}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>

      {selected ? (
        <CareResolvePanel
          key={selected.row.subCaseUrn}
          desk={selected}
          disruptionUrn={disruptionUrn}
          board={board}
        />
      ) : (
        <div className="hidden items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground lg:flex">
          {blocked.length > 0 ? t('queue.emptyPanel.blocked') : t('queue.emptyPanel.plain')}
        </div>
      )}
    </div>
  );
}

/** One queue row — two compact lines, the whole row is the button. */
function QueueRow({
  desk,
  selected,
  onSelect,
  override,
  onOpenOverride,
}: {
  desk: CareDeskRow;
  selected: boolean;
  onSelect: () => void;
  override?: { overrideUrn: string; row: BenefitOverrideApplicationRow } | undefined;
  onOpenOverride?: ((overrideUrn: string) => void) | undefined;
}) {
  const { t } = useTranslation('care');
  const { row, lane } = desk;
  const chips = ssrChips(desk);
  const reason = careLaneReason(desk);
  const ref = useRef<HTMLLIElement>(null);
  // Keep the selected row visible — a counter-bar / deep-link selection may land
  // far down the list. `nearest` no-ops when it's already on screen.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);
  return (
    <li ref={ref}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        data-testid={`care-row-${row.pnrLabel}`}
        className={cn(
          'w-full rounded-md border px-2.5 py-1.5 text-left transition-colors',
          selected
            ? 'border-primary bg-primary/5'
            : 'border-transparent hover:border-border hover:bg-muted/50',
        )}
      >
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className={cn('h-2 w-2 shrink-0 rounded-full', LANE_DOT[lane] ?? 'bg-muted-foreground')}
          />
          <span className="font-mono text-sm font-semibold">{row.pnrLabel}</span>
          <PnrSourceMark source={row.pnrSource} className="shrink-0" testId="care-row-source" />
          {(row.priorityTags ?? []).slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="shrink-0 rounded bg-violet-100 px-1 py-px text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
            >
              {priorityShortLabel(tag)}
            </span>
          ))}
          {chips.slice(0, 3).map((code) => (
            <span
              key={code}
              className="shrink-0 rounded bg-muted px-1 py-px text-[10px] font-semibold uppercase text-muted-foreground"
              title={t('queue.ssrTitle')}
            >
              {code}
            </span>
          ))}
          {override ? (
            // #819 — the override's result for this group; the chip opens the record.
            <Badge
              variant={OUTCOME_VARIANT[override.row.outcome]}
              size="sm"
              role="link"
              tabIndex={0}
              title={t('override.chip.title', {
                urn: override.overrideUrn.split(':').pop(),
                outcome: t(outcomeLabelKey(override.row.outcome)),
              })}
              onClick={(e) => {
                e.stopPropagation();
                onOpenOverride?.(override.overrideUrn);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenOverride?.(override.overrideUrn);
                }
              }}
              data-testid={`care-override-chip-${row.pnrLabel}`}
              className="shrink-0 cursor-pointer"
            >
              {t(outcomeLabelKey(override.row.outcome))}
              {override.row.amountUsd > 0 ? ` +${override.row.amountUsd}` : ''}
            </Badge>
          ) : null}
          <span className="min-w-0 flex-1 truncate text-right text-xs text-muted-foreground">
            {t('queue.paxFlight', { pax: row.paxCount, flight: row.flightLabel })}
          </span>
        </span>
        <span className="mt-0.5 block truncate pl-4 text-xs text-muted-foreground">
          {reason ? t(reason.key, { ...reason.params }) : null}
        </span>
      </button>
    </li>
  );
}
